import { DocumentNode, gql, useMutation, useQuery } from '@apollo/client';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Heading,
  Stack,
  StackDivider,
  Text,
  useToast,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import yn from 'yn';

import { TabLink, TabLinkList } from '@/components/TabLink';
import { usePermissions } from '@/hooks/usePermissions';
import { useSignUpPrompt } from '@/hooks/useSignUpPrompt';
import { logError } from '@/imports/logging/ClientLogger';
import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { ChevronLeftIcon } from '@/imports/ui/chakra/feather';

import { GroupHeader_CurrentUser } from './graphql-types/GroupHeader_CurrentUser';
import { GroupHeader_GroupFragment } from './graphql-types/GroupHeader_GroupFragment';

import { GroupHeaderBanner } from './GroupHeaderBanner/GroupHeaderBanner';
import { GroupHeaderDropdown } from './GroupHeaderBanner/GroupHeaderDropdown';

const FOLLOW_GROUP = gql`
  mutation GroupHeader_CreateGroupLinkMutation(
    $input: CreateUserGroupLinkInput!
  ) {
    createUserGroupLink(input: $input) {
      data {
        _id
        user {
          _id
          userOnboardingChecklistState {
            userHasJoinedAGroup
          }
        }
      }
    }
  }
`;

const GROUP_FRAGMENT = gql`
  fragment GroupHeader_GroupFragment on Group {
    _id
    name
    description
    ...GroupHeaderBanner_GroupFragment
    ...GroupHeaderDropdown_GroupFragment
    currentUserGroupPermission {
      canJoin
      canViewContent
    }
    currentUserGroupLink {
      _id
    }
    subgroups {
      groupNodes {
        group {
          _id
        }
      }
    }
    parentGroup {
      _id
      name
    }
  }
  ${GroupHeaderBanner.groupFragment}
  ${GroupHeaderDropdown.groupFragment}
`;

const CURRENT_USER = gql`
  query GroupHeader_CurrentUser {
    currentUser {
      _id
    }
  }
`;

export const GroupHeader: React.FC<{
  group: GroupHeader_GroupFragment;
}> & { groupFragment: DocumentNode } = ({ group }) => {
  const toast = useToast();
  const permissions = usePermissions();

  // Note that this is not ideally how we would handle eagerly joining a group
  // The GroupHeader component has no business needing to implement this logic
  // and ideally all this should be done in some sort of custom hook that is aware
  // of the route and can handle all this logic itself (and we could inject that logic somewhere else)
  // https://github.com/TradeWing/tradewing/pull/3248#discussion_r658196424
  const router = useRouter();

  const { data, loading } = useQuery<GroupHeader_CurrentUser, {}>(CURRENT_USER);

  const [createUserGroupLink, createUserGroupLinkResult] =
    useMutation(FOLLOW_GROUP);

  const {
    promptModal,
    loading: signUpPromptLoading,
    showPrompt,
  } = useSignUpPrompt();

  const canJoinGroup = group.currentUserGroupPermission?.canJoin;
  const userIsInGroup = group.currentUserGroupLink;

  const analyticsClient = useAnalyticsLogger();
  const currentUser = data?.currentUser;

  const onClick = React.useCallback(async (): Promise<void> => {
    if (!currentUser) {
      showPrompt();
      return;
    }

    try {
      await createUserGroupLink({
        variables: {
          input: {
            data: {
              userId: currentUser._id,
              groupId: group._id,
            },
          },
        },
      });

      analyticsClient.logUserJoinedGroup();

      toast({ status: 'success', description: 'Successfully joined group.' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to join group.' });
      logError(err);
    }
  }, [
    analyticsClient,
    createUserGroupLink,
    currentUser,
    group._id,
    showPrompt,
    toast,
  ]);

  React.useEffect(() => {
    const shouldEagerlyJoinGroup = router.isReady && yn(router.query.joinGroup);
    if (
      shouldEagerlyJoinGroup &&
      canJoinGroup &&
      !userIsInGroup &&
      !createUserGroupLinkResult.called
    ) {
      onClick().catch((err) => logError(err));
    }
  }, [router, canJoinGroup, userIsInGroup, onClick, createUserGroupLinkResult]);

  if (loading || signUpPromptLoading || permissions.isLoading) {
    return <Loading />;
  }

  const hasSubgroups = group.subgroups.groupNodes.length > 0;

  return (
    <Card>
      {promptModal}
      <GroupHeaderBanner group={group} />
      <Stack spacing="0" divider={<StackDivider />}>
        <Stack p="4">
          {!!group.parentGroup && (
            <Breadcrumb separator={<ChevronLeftIcon />} color="blue.600">
              {group.parentGroup && (
                <BreadcrumbItem>
                  <BreadcrumbLink
                    as={NextLink}
                    href={`/community/groups/${group.parentGroup._id}/activity`}
                  >
                    {group.parentGroup.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              )}

              <BreadcrumbItem>
                <BreadcrumbLink
                  as={NextLink}
                  href={`/community/groups/${group._id}/activity`}
                >
                  {group.name}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          )}
          <Stack>
            <Stack
              direction="row"
              alignItems="flex-end"
              justifyContent="space-between"
            >
              <Heading
                size="md"
                color="gray.700"
                noOfLines={1}
                wordBreak="break-all"
              >
                {group.name}
              </Heading>
              <GroupHeaderDropdown group={group} />
            </Stack>
            <Text whiteSpace="pre-wrap" color="gray.600">
              {group.description}
            </Text>
          </Stack>
        </Stack>
        {group.currentUserGroupPermission?.canViewContent && (
          <TabLinkList px="4">
            <TabLink href={`/community/groups/${group._id}/activity`}>
              Activity
            </TabLink>
            <TabLink href={`/community/groups/${group._id}/members`}>
              Members
            </TabLink>
            <TabLink href={`/community/groups/${group._id}/resources`}>
              Resources
            </TabLink>
            <TabLink href={`/community/groups/${group._id}/events`}>
              Events
            </TabLink>
            {hasSubgroups && (
              <TabLink href={`/community/groups/${group._id}/subgroups`}>
                Subgroups
              </TabLink>
            )}
          </TabLinkList>
        )}
      </Stack>
    </Card>
  );
};

GroupHeader.groupFragment = GROUP_FRAGMENT;
